import { useCallback, useRef } from "react";
import { FiX } from "react-icons/fi";
import { RichTextEditor } from "newui/components/ui/RichTextEditor";
import { KeepOriginalChip } from "./KeepOriginalChip";
import type { FinalCriterionScore } from "../../../types/scoring.type";

interface FinalCriterionScoringRowProps {
	criterion: FinalCriterionScore;
	maxScore: number;
	readOnly?: boolean;
	onFinalScoreChange: (rfxCriteriaId: number, score: number | null) => void;
	onFinalCommentChange: (rfxCriteriaId: number, comment: string | null) => void;
}

export const FinalCriterionScoringRow: React.FC<FinalCriterionScoringRowProps> = ({
	criterion,
	maxScore,
	readOnly = false,
	onFinalScoreChange,
	onFinalCommentChange,
}) => {
	const rowRef = useRef<HTMLDivElement>(null);

	const handleScoreChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const raw = e.target.value.trim();
			if (raw === "") {
				onFinalScoreChange(criterion.rfxCriteriaId, null);
				return;
			}
			const parsed = parseInt(raw, 10);
			if (isNaN(parsed)) {
				return;
			}
			onFinalScoreChange(criterion.rfxCriteriaId, parsed);
		},
		[criterion.rfxCriteriaId, onFinalScoreChange]
	);

	const handleCommentChange = useCallback(
		(html: string) => {
			const isEmpty = !html || html === "<p><br></p>" || html.trim() === "";
			onFinalCommentChange(criterion.rfxCriteriaId, isEmpty ? null : html);
		},
		[criterion.rfxCriteriaId, onFinalCommentChange]
	);

	const handleKeepOriginal = useCallback(() => {
		if (criterion.initialScore !== null) {
			onFinalScoreChange(criterion.rfxCriteriaId, criterion.initialScore);
		}
	}, [criterion.rfxCriteriaId, criterion.initialScore, onFinalScoreChange]);

	return (
		<div
			ref={rowRef}
			className="rounded-lg border border-gray-200 p-4 space-y-3"
			data-testid={`final-criterion-row-${criterion.rfxCriteriaId}`}
		>
			<div className="flex flex-wrap items-center gap-3">
				<span className="text-sm font-semibold text-gray-900">
					{criterion.criterionName}
				</span>
				<span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
					Weight: {criterion.criterionWeight}%
				</span>

				{criterion.initialScore !== null && (
					<span
						className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
						data-testid={`initial-score-badge-${criterion.rfxCriteriaId}`}
					>
						Initial: {criterion.initialScore}
					</span>
				)}

				<div className="flex items-center gap-2">
					<label
						htmlFor={`final-score-${criterion.rfxCriteriaId}`}
						className="text-xs font-medium text-gray-600"
					>
						Final Score (0–{maxScore})
					</label>
					<div className="relative">
						<input
							id={`final-score-${criterion.rfxCriteriaId}`}
							type="number"
							min={0}
							max={maxScore}
							step="1"
							value={criterion.finalScore ?? ""}
							onChange={handleScoreChange}
							disabled={readOnly}
							className="w-20 rounded-md border border-gray-300 px-2 py-1 pr-7 text-sm text-right focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
							data-testid={`final-score-input-${criterion.rfxCriteriaId}`}
							placeholder="—"
						/>
						{criterion.finalScore !== null && !readOnly && (
							<button
								type="button"
								onClick={() => onFinalScoreChange(criterion.rfxCriteriaId, null)}
								className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
								aria-label="Clear final score"
								data-testid={`final-score-clear-${criterion.rfxCriteriaId}`}
							>
								<FiX size={12} />
							</button>
						)}
					</div>

					{criterion.finalScore === null && criterion.initialScore !== null && !readOnly && (
						<KeepOriginalChip
							initialScore={criterion.initialScore}
							onKeep={handleKeepOriginal}
						/>
					)}
				</div>
			</div>

			<div data-testid={`final-comment-editor-${criterion.rfxCriteriaId}`}>
				<RichTextEditor
					value={criterion.finalComment ?? criterion.initialComment ?? ""}
					onChange={handleCommentChange}
					disabled={readOnly}
					placeholder="Add final comments..."
					minHeight="4rem"
					testId={`final-comment-rte-${criterion.rfxCriteriaId}`}
				/>
			</div>
		</div>
	);
};
